import { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Dropzone from "src/components/organization/dropzone/Dropzone";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { Upload } from "tus-js-client";
import LinearProgressWithLabel from "src/components/atoms/LinearProgressWithLabel";
import { useAppSelector, useAppDispatch } from "src/app/reduxCustomHooks";
import { logoutAction } from "src/app/slices/authSlice";
import { useNavigate } from "react-router";
import { useLazyGetApiUploadedFilesQuery } from "src/app/services/api";
import BreadCrumbs from "src/components/organization/layout/breadcrumbs/BreadCrumbs";
import CustomDropDown from "src/components/organization/CustomDropDown";
import {
  useGetChannelsQuery,
  usePostApiConvertTusFileMutation,
  useUploalFileFromUrlMutation,
} from "src/app/services/api.generated";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

export const url = process.env.REACT_APP_PRODUCTION_URL_V1
  // process.env.NODE_ENV === "development"
  //   ? process.env.REACT_APP_DEVELOPMENT_URL_V1
  //   : process.env.REACT_APP_PRODUCTION_URL_V1;

export default function UploadFile() {
  const [channel, setChannel] = useState("");
  const [uploadType, setUploadType] = useState("manual");
  const [files, setFiles] = useState<File[]>([]);
  const { data: channels } = useGetChannelsQuery();
  const [isLoading, setIsLoading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  const [fetchUploadedFiles] = useLazyGetApiUploadedFilesQuery();
  const [uploalFileFromUrl, { data, isLoading: uploadWithLinkLoading }] =
    useUploalFileFromUrlMutation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(({ auth }) => auth);
  const [convertFile] = usePostApiConvertTusFileMutation();
  const [counter, setCounter] = useState(1);
  const [items, setItems] = useState([{ id: 1, title: "", link: "" }]);

  const handleAdd = () => {
    setCounter((prev) => prev + 1);
    setItems((prev) => [...prev, { id: counter + 1, title: "", link: "" }]);
  };

  const handleDelete = (id: number) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((item) => item.id !== id));
    }
  };

  useEffect(() => {
    if (data) {
      if (data?.errorId === 0) {
        toast.success(data?.result);
        navigate("/files/uploaded-files");
      } else {
        toast.error(data?.errorTitle);
      }
    }
  }, [data]);

  const submit = async () => {
    if (uploadType === "manual") {
      if (files && files.length > 0 && channel !== "") {
        setIsLoading(true);
        setUploadingFiles(files);

        files.forEach((file) => {
          const upload = new Upload(file, {
            endpoint: `${url}/tus`,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            metadata: {
              filename: file.name,
              filetype: file.type,
              channelId: channel,
              channelName: channels?.find((c) => c._id === channel)?.title,
            },
            headers: { Authorization: `Bearer ${auth.token}` },
            onError: (error) => {
              if (error.message.includes("401")) {
                toast.error("توکن کاربری معتبر نیست");
                dispatch(logoutAction());
              } else {
                toast.error("اشکالی در ارتباط با سرور پیش آمده");
              }
              setIsLoading(false);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
              const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: +percentage,
              }));
            },
            onSuccess: async () => {
              // const convertData = [
              //   { bitrate: "1000k", quality: "1280:720" },
              //   { bitrate: "2000k", quality: "1920:1080" },
              //   { bitrate: "500k", quality: "842:480" },
              // ];

              // console.log("Upload completed:", upload.url);
              // const parts = upload.url ? upload.url.split("/") : "";
              // const id = parts[parts.length - 1];

              // convertData.map(({ bitrate, quality }) => {
              //   convertFile({
              //     tusFileId: id || "",
              //     bitrate,
              //     quality,
              //   });
              // });

              fetchUploadedFiles()
                .unwrap()
                .then(() => {
                  toast.success(`${file.name} با موفقیت آپلود شد`);
                })
                .catch((err) => console.log(err));

              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: 100,
              }));
              setIsLoading(false);
            },
          });

          upload.findPreviousUploads().then((previousUploads) => {
            if (previousUploads.length)
              upload.resumeFromPreviousUpload(previousUploads[0]);
            upload.start();
          });
        });
      } else if (channel === "") {
        toast.warning("لطفا دسته بندی را انتخاب نمایید");
      } else if (!files || files.length === 0) {
        toast.warning("لطفا فایل‌های مورد نظر خود را وارد نمایید");
      }
    } else if (uploadType === "link") {
      if (!channel) {
        toast.warning("لطفا دسته بندی را انتخاب نمایید");
        return null;
      }
      if (items.length > 0) {
        const links = items.map((l) => l.link);
        const titles = items.map((t) => t.title);
        if (links.includes("")) {
          toast.warning("لطفا url مورد نظر را وارد نمایید");
        } else if (titles.includes("")) {
          toast.warning("لطفا عنوان را وارد نمایید");
        } else {
          items.map((item) => {
            uploalFileFromUrl({
              url: item.link,
              title: item.title,
              channelId: channel,
            });
          });
        }
      }
    }
  };

  return (
    <>
      <Stack
        sx={{ width: "100%" }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <BreadCrumbs spaceBottom={false} />
        <Button
          variant="contained"
          onClick={() => navigate("/files/uploaded-files")}
        >
          بازگشت
        </Button>
      </Stack>
      <br />
      <Paper
        sx={{
          mb: 2,
          borderRadius: "4px",
          boxShadow:
            "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        }}
      >

        <Stack
          direction="column"
          alignItems="center"
          sx={{
            width: "100%",
            backgroundColor: "#7986cb",
            height: "48px",
            borderColor: "#7986cb",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Typography
            sx={{
              margin: "auto 0",
              fontSize: "1.25rem",
              lineHeight: "1.5",
              color: "#fff",
              fontFamily: "iransansxv",
            }}
          >
            ویدیو جدید
          </Typography>
        </Stack>
        <Stack width="100%" padding="16px">
          <Stack
            width="100%"
            display="flex"
            justifyContent="space-between"
            flexDirection="row"
            alignItems="flex-start"
          >
            <FormControl sx={{ mb: 2, flexShrink: "0" }}>
              <FormLabel>نوع آپلود : </FormLabel>
              <RadioGroup
                row
                value={uploadType}
                onChange={(e: any) => {
                  setUploadType(e.target.value);
                }}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="فایل انتخابی"
                />
                <FormControlLabel
                  value="link"
                  control={<Radio />}
                  label="لینک"
                />
              </RadioGroup>
            </FormControl>
            <CustomDropDown
              value={channel}
              setValue={setChannel}
              items={channels ?? []}
              label="دسته بندی ها"
            />
          </Stack>

          {uploadType === "manual" ? (
            <Dropzone
              description="فایل ویدیوی خود را در اینجا آپلود کنید"
              onChange={setFiles}
            />
          ) : (
            <>
              {items.map((item, index) => (
                <Stack
                  key={item.id}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  width="100%"
                  marginBottom="10px"
                  gap="5px"
                >
                  <TextField
                    sx={{ flex: "1" }}
                    placeholder="عنوان ویدیو"
                    label="عنوان ویدیو"
                    value={item.title}
                    onChange={(e) => {
                      const value = e.target.value;
                      setItems((prev) =>
                        prev.map((p) =>
                          p.id === item.id ? { ...p, title: value } : p
                        )
                      );
                    }}
                  />
                  <TextField
                    sx={{ flex: "4" }}
                    placeholder="لینک ویدیو"
                    label="لینک ویدیو"
                    value={item.link}
                    onChange={(e) => {
                      const value = e.target.value;
                      setItems((prev) =>
                        prev.map((p) =>
                          p.id === item.id ? { ...p, link: value } : p
                        )
                      );
                    }}
                  />
                  <IconButton
                    aria-label="delete"
                    color="primary"
                    onClick={() => handleDelete(item.id)}
                  >
                    <DeleteIcon />
                  </IconButton>

                  {index === items.length - 1 && (
                    <IconButton
                      onClick={handleAdd}
                      aria-label="add"
                      color="primary"
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </>
          )}

          {uploadType === "manual" &&
            uploadingFiles.map((file) => (
              <Stack key={file.name} sx={{ mt: 2 }}>
                <Typography>{file.name}</Typography>
                <LinearProgressWithLabel value={uploadProgress[file.name] || 0} />
              </Stack>
            ))}

          <LoadingButton
            loading={isLoading || uploadWithLinkLoading}
            variant="contained"
            sx={{ width: 250, height: 50, fontSize: "16px", mt: 3 }}
            onClick={submit}
          >
            تایید
          </LoadingButton>
        </Stack>
      </Paper>
    </>
  );
}
